export interface PositionRecord {
  psaRecordIndex: number;
  parentFixturePsaRecordIndex: number;
  rawRecord?: string;
  upc: string;
  dbParentProductKey: number;
  dbKey: number; // The unique identifier for the position

  field2: number;
  field3: string;

  x: number; // The X location for the position, measured from the left edge of the planogram
  width: number; // The overall width of the position
  y: number; // The Y location for the position, measured from the bottom of the planogram
  height: number; // The overall height of the position
  z: number; // The Z location for the position, measured from the back of the planogram
  depth: number; // The overall depth of the position

  field10: number;
  field11: number;
  field12: number;
  // slope: number; // The amount the position is rotated, as seen from the side view
  // angle: number; // The amount to rotate the position, in degrees, as viewed from above
  // roll: number; // The amount to rotate the position from the front, in degrees. This field is limited to 89 degrees to avoid confusion with the orientation system
  // changed: number; // Specifies whether position data has changed during the current Space Planning session
  merchStyle: number; // The merchandising style of the position. Options are Unit, Tray, Case, Display, Alternate, and Loose. 0-Unit, 1-tray, 2-case etc.
  hFacings: number; // The number of facings wide for the position
  vFacings: number; // The number of vertical facings for the position
  dFacings: number; // The number of facings deep for the position
  xCapNum: number; // The number of horizontal caps or nests for the position
  xCapNested: number; // Specifies whether horizontal caps are nested
  xCapReversed: number; // Specifies whether the horizontal caps or nests are on the left or right of the position
  xCapOrientation: number; // The horizontal orientation of caps or nests
  yCapNum: number; // The number of vertical caps or nests for the position
  yCapNested: number; // Specifies whether vertical caps are nested
  yCapReversed: number; // Specifies whether the vertical caps or nests are at the top or bottom of the position
  yCapOrientation: number; // The vertical orientation of caps or nests
  zCapNum: number; // The number of depth caps or nests
  zCapNested: number; // Specifies whether depth caps are nests
  zCapReversed: number; // Specifies whether depth caps or nests are in the front or back of the position
  zCapOrientation: number; // The orientation of the depth caps or nests
  orientation: number; // The orientation of the position; Direction product is facing, refernece orientation as numerical value; 0=Front, 1=Front90
  jumbleWidth: number; // Not currently used
  jumbleHeight: number; // Not currently used
  jumbleDepth: number; // Not currently used
  merchStyleWidth: number; // The width of a single facing of the merchandising style in its orientation
  merchStyleHeight: number; // The height of a single facing of the merchandising style in its orientation
  merchStyleDepth: number; // The depth of a single facing of the merchandising style in its orientation
  fullWidth: number; // The width of a single unit, including the inter-unit spacing
  fullHeight: number; // The height of a single unit, including the inter-unit spacing
  fullDepth: number; // The depth of a single unit, including the inter-unit spacing
  xSubUnits: number; // The number of real units wide contained in the merchandised unit; Inner units for tray/case etc.
  ySubUnits: number; // The number of real units high contained in the merchandised unit; Inner units for tray/case etc.
  zSubUnits: number; // The number of real units deep contained in the merchandised unit; Inner units for tray/case etc.
  pegId: string; // The peg assigned to the position

  // 8 fields in headers file but only 6 used
  field43: number;
  field44: number;
  field45: number;
  field46: number;
  field47: number;
  field48: number;
  // row: number; // The pegboard row on which the position is placed
  // col: number; // The pegboard column on which the position is placed
  // manualUnits: number; // The manually determined facings
  // rankX: number; // The sequential horizontal order of the position
  // rankY: number; // The sequential vertical order of the position
  // rankZ: number; // The sequential depth order of the position
  // pegSpan: number; // The number of horizontal peg holes required by the position
  // alwaysFloat: number; // Specifies whether the position should float on the planogram
  // end 8 fields in headers file but only 6 used

  primaryPositionLabelFormatName: string; // The primary label format used for the position. If set to <default>, Space Planning uses the default fixture label in the active label file.
  secondaryPositionLabelFormatName: string; // The secondary label format used for the position. If set to <default>, Space Planning uses the default fixture label in the active label file.
  merchXMin: number; // The minimum number of horizontal facings of child positions
  merchXMax: number; // The maximum number of horizontal facings of child positions, including horizontal caps
  merchXUprights: number; // The maximum number of horizontal upright units, used to specify the target number of upright facings in conjunction with horizontal capping
  merchXCaps: number; // The maximum number of horizontal caps for positions
  merchXPlacement: number; // Specifies how positions are placed horizontally
  merchXNumber: number; // Specifies how the number of horizontal units of child positions is controlled
  merchXSize: number; // Specifies how the width of the facings in a position is controlled
  merchXDirection: number; // Specifies whether to base merchandising from the right instead of the left
  merchXSqueeze: number; // Specifies whether individual facings can be squeezed horizontally
  merchYMin: number; // The minimum number of vertical facings of child positions
  merchYMax: number; // The maximum number of vertical facings of child positions
  merchYUprights: number; // The maximum number of normal upright units vertically, used to specify the target number of upright units high in conjunction with vertical capping
  merchYCaps: number; // The maximum vertical number of caps
  merchYPlacement: number; // Specifies how the positions are placed vertically
  merchYNumber: number; // Specifies how the number of vertical units of child positions is controlled
  merchYSize: number; // Specifies how the height of the units in a position is controlled
  merchYDirection: number; // Specifies whether to base merchandising from the top instead of the base
  merchYSqueeze: number; // Specifies whether individual facings can be squeezed vertically
  merchZMin: number; // The minimum number of units deep of child positions
  merchZMax: number; // The maximum number of units deep of child positions, including depth caps
  merchZUprights: number; // The maximum number of upright units deep
  merchZCaps: number; // The maximum number of caps deep
  merchZPlacement: number; // Specifies how positions are placed depth-wise
  merchZNumber: number; // Specifies how the number of units deep of child positions is controlled
  merchZSize: number; // Specifies how the depth of the units in a position is controlled
  merchZDirection: number; // Specifies whether to base merchandising from the back instead of the front
  merchZSqueeze: number; // Specifies whether individual facings can be squeezed depth-wise
  // warning: string; // Specifies if there is a warning on the position
  // warningNumber: number; // If there is a warning, The number of that warning
  desc1: string; // 1/4 Pallet ID
  desc2: string; // 1/2 Pallet ID
  desc3: string; // Pallet ID
  desc4: string; // One Touch Rack ID
  desc5: string; // ShipperShipper ID
  desc6: string; // SRI Requested Date
  desc7: string; // SRI Comment
  desc8: string; // PPI Comment
  desc9: string; // No description
  desc10: string; // PositionChangeType
  desc11: string; // PreviousShelf
  desc12: string; // No description
  desc13: string; // No description
  desc14: string; // No description
  desc15: string; // No description
  desc16: string; // No description
  desc17: string; // No description
  desc18: string; // No description
  desc19: string; // No description
  desc20: string; // No description
  desc21: string; // No description
  desc22: string; // No description
  desc23: string; // No description
  desc24: string; // No description
  desc25: string; // No description
  desc26: string; // No description
  desc27: string; // No description
  desc28: string; // No description
  desc29: string; // No description
  desc30: string; // No description
  value1: number; // PositionXChange
  value2: number; // PositionYChange
  value3: number; // PositionFacingChange
  value4: number; // PositionRowHighChange
  value5: number; // PreviousOrientation
  value6: number; // PreviousMerchStyle
  value7: number; // PreviousSegment
  value8: number; // PPI
  value9: number; // LEB Exception
  value10: number; // No description
  value11: number; // No description
  value12: number; // No description
  value13: number; // No description
  value14: number; // No description
  value15: number; // No description
  value16: number; // No description
  value17: number; // No description
  value18: number; // No description
  value19: number; // No description
  value20: number; // No description
  value21: number; // No description
  value22: number; // No description
  value23: number; // No description
  value24: number; // No description
  value25: number; // No description
  value26: number; // No description
  value27: number; // No description
  value28: number; // No description
  value29: number; // No description
  value30: number; // No description
  flag1: number; // Quarter Pallet
  flag2: number; // Half Pallet
  flag3: number; // DRP
  flag4: number; // One Touch Rack
  flag5: number; // Shipper
  flag6: number; // Store Requested Item
  flag7: number; // RRP
  flag8: number; // Wine Flag
  flag9: number; // No description
  flag10: number; // Security Case

  field148: number;
  field149: number;
  field150: number;
  field151: number;
  field152: number;
  field153: number;
  field154: number;
  field155: number;
  field156: number;
  field157: number;
  field158: string;
  field159: string;
  field160: number;
  field161: string;
  field162: number;
  field163: number;
  field164: number;
  field165: number;
  field166: number;
  // locationId: number; // The location ID given to the position
  // useTargetSpaceX: number; // Specifies whether the horizontal Target Space option is being used
  // useTargetSpaceY: number; // Specifies whether the vertical Target Space option is being used
  // useTargetSpaceZ: number; // Specifies whether the depth Target Space option is being used
  // targetSpaceX: number; // The amount of horizontal space to be filled by the position
  // targetSpaceY: number; // The amount of vertical space to be filled by the position
  // targetSpaceZ: number; // The amount of depth space to be filled by the position
  // capacity: number; // The total number of units that occupy this position on the planogram
  // units: number; // The number of units of the position (entered manually)
  // linear: number; // The total linear space occupied by the position
  // square: number; // The total square space occupied by the position.
  // cubic: number; // The total cubic space for this position
  // targetInventory: number; // The recommended number of units for this position based on the inventory model settings
  // facings: number; // The total number of front units for this position
  // indexInPlanogram: number; // The number of the position in the planogram, indicating the order in which positions were added to the planogram
  // segment: number; // The segment in which the position’s midpoint falls
  // firstSegment: number; // The first segment in which the position is displayed
  // lastSegment: number; // The last segment in which the position is displayed
  // imageSource: string; // The image associated with the position
  // pegIdUsed: string; // The actual peg used for the position when placed on a pegboard, multi-row pegboard, or hanging bar
  // replenishmentMin: number; // The minimum number of units required to require an automatic restocking for the product in the store
  // replenishmentMax: number; // The maximum number of units allowed to require an automatic restocking for the product in the store
  // shapeId: string; // The shape associated with the individual position. This field overrides the specified shape for the product and allows you to display different shapes for multiple positions of the same product.
  // bitmapIdOverride: string; // The image associated with the individual position. This field overrides the specified image for the product and allows you to display different images for multiple positions of the same product.
  // hideIfPrinting: number; // Specifies whether to draw the position when printing a Front, Side, or Top view
  // partid: string; // An additional field for storing an identifier of the position
  // bitmapIdOverrideUnit: number; // Specifies whether the image for the position represents a single unit. When selected, if the Show Units Within Merchandising Style option is selected, the unit image is copied high, wide, or deep when the product is merchandised in a tray or case.
  // capacityUnrestricted: number; // The number of units that would fit for the position if no other merchandising restrictions (such as Max High) were applied.
  // automaticModel: number; // Specifies how to draw automatic models for positions of the product, when using the Jar, Can, or Bottle merchandising style and specific models are not assigned for the product or position
}

export const positionRecordValidation = {
  upc: "VARCHAR",
  dbParentProductKey: "BIGINT",
  dbKey: "BIGINT",
  x: "NUMERIC", // The X location for the position, measured from the left edge of the planogram
  width: "NUMERIC", // The overall width of the position
  y: "NUMERIC", // The Y location for the position, measured from the bottom of the planogram
  height: "NUMERIC", // The overall height of the position
  z: "NUMERIC", // The Z location for the position, measured from the back of the planogram
  depth: "NUMERIC", // The overall depth of the position

  field10: "NUMERIC",
  field11: "NUMERIC",
  field12: "NUMERIC",
  // slope: "NUMERIC", // The amount the position is rotated, as seen from the side view
  // angle: "NUMERIC", // The amount to rotate the position, in degrees, as viewed from above
  // roll: "NUMERIC", // The amount to rotate the position from the front, in degrees. This field is limited to 89 degrees to avoid confusion with the orientation system
  // changed: "INT", // Specifies whether position data has changed during the current Space Planning session
  merchStyle: "INT", // The merchandising style of the position. Options are Unit, Tray, Case, Display, Alternate, and Loose. 0-Unit, 1-tray, 2-case etc.
  hFacings: "INT", // The number of facings wide for the position
  vFacings: "INT", // The number of vertical facings for the position
  dFacings: "INT", // The number of facings deep for the position
  xCapNum: "INT", // The number of horizontal caps or nests for the position
  xCapNested: "INT", // Specifies whether horizontal caps are nested
  xCapReversed: "INT", // Specifies whether the horizontal caps or nests are on the left or right of the position
  xCapOrientation: "INT", // The horizontal orientation of caps or nests
  yCapNum: "INT", // The number of vertical caps or nests for the position
  yCapNested: "INT", // Specifies whether vertical caps are nested
  yCapReversed: "INT", // Specifies whether the vertical caps or nests are at the top or bottom of the position
  yCapOrientation: "INT", // The vertical orientation of caps or nests
  zCapNum: "INT", // The number of depth caps or nests
  zCapNested: "INT", // Specifies whether depth caps are nests
  zCapReversed: "INT", // Specifies whether depth caps or nests are in the front or back of the position
  zCapOrientation: "INT", // The orientation of the depth caps or nests
  orientation: "INT", // The orientation of the position; Direction product is facing, refernece orientation as numerical value; 0=Front, 1=Front90
  jumbleWidth: "NUMERIC", // Not currently used
  jumbleHeight: "NUMERIC", // Not currently used
  jumbleDepth: "NUMERIC", // Not currently used
  merchStyleWidth: "NUMERIC", // The width of a single facing of the merchandising style in its orientation
  merchStyleHeight: "NUMERIC", // The height of a single facing of the merchandising style in its orientation
  merchStyleDepth: "NUMERIC", // The depth of a single facing of the merchandising style in its orientation
  fullWidth: "NUMERIC", // The width of a single unit, including the inter-unit spacing
  fullHeight: "NUMERIC", // The height of a single unit, including the inter-unit spacing
  fullDepth: "NUMERIC", // The depth of a single unit, including the inter-unit spacing
  xSubUnits: "INT", // The number of real units wide contained in the merchandised unit; Inner units for tray/case etc.
  ySubUnits: "INT", // The number of real units high contained in the merchandised unit; Inner units for tray/case etc.
  zSubUnits: "INT", // The number of real units deep contained in the merchandised unit; Inner units for tray/case etc.
  pegId: "VARCHAR", // The peg assigned to the position

  // 8 fields in headers file but only 6 used
  field43: "NUMERIC",
  field44: "NUMERIC",
  field45: "NUMERIC",
  field46: "NUMERIC",
  field47: "NUMERIC",
  field48: "NUMERIC",
  // row: "NUMERIC", // The pegboard row on which the position is placed
  // col: "NUMERIC", // The pegboard column on which the position is placed
  // manualUnits: "INT", // The manually determined facings
  // rankX: "INT", // The sequential horizontal order of the position
  // rankY: "INT", // The sequential vertical order of the position
  // rankZ: "INT", // The sequential depth order of the position
  // pegSpan: "INT", // The number of horizontal peg holes required by the position
  // alwaysFloat: "INT", // Specifies whether the position should float on the planogram
  // end 8 fields in headers file but only 6 used

  primaryPositionLabelFormatName: "VARCHAR", // The primary label format used for the position. If set to <default>, Space Planning uses the default fixture label in the active label file.
  secondaryPositionLabelFormatName: "VARCHAR", // The secondary label format used for the position. If set to <default>, Space Planning uses the default fixture label in the active label file.
  merchXMin: "INT", // The minimum number of horizontal facings of child positions
  merchXMax: "INT", // The maximum number of horizontal facings of child positions, including horizontal caps
  merchXUprights: "INT", // The maximum number of horizontal upright units, used to specify the target number of upright facings in conjunction with horizontal capping
  merchXCaps: "INT", // The maximum number of horizontal caps for positions
  merchXPlacement: "INT", // Specifies how positions are placed horizontally
  merchXNumber: "INT", // Specifies how the number of horizontal units of child positions is controlled
  merchXSize: "INT", // Specifies how the width of the facings in a position is controlled
  merchXDirection: "INT", // Specifies whether to base merchandising from the right instead of the left
  merchXSqueeze: "INT", // Specifies whether individual facings can be squeezed horizontally
  merchYMin: "INT", // The minimum number of vertical facings of child positions
  merchYMax: "INT", // The maximum number of vertical facings of child positions
  merchYUprights: "INT", // The maximum number of normal upright units vertically, used to specify the target number of upright units high in conjunction with vertical capping
  merchYCaps: "INT", // The maximum vertical number of caps
  merchYPlacement: "INT", // Specifies how the positions are placed vertically
  merchYNumber: "INT", // Specifies how the number of vertical units of child positions is controlled
  merchYSize: "INT", // Specifies how the height of the units in a position is controlled
  merchYDirection: "INT", // Specifies whether to base merchandising from the top instead of the base
  merchYSqueeze: "INT", // Specifies whether individual facings can be squeezed vertically
  merchZMin: "INT", // The minimum number of units deep of child positions
  merchZMax: "INT", // The maximum number of units deep of child positions, including depth caps
  merchZUprights: "INT", // The maximum number of upright units deep
  merchZCaps: "INT", // The maximum number of caps deep
  merchZPlacement: "INT", // Specifies how positions are placed depth-wise
  merchZNumber: "INT", // Specifies how the number of units deep of child positions is controlled
  merchZSize: "INT", // Specifies how the depth of the units in a position is controlled
  merchZDirection: "INT", // Specifies whether to base merchandising from the back instead of the front
  merchZSqueeze: "INT", // Specifies whether individual facings can be squeezed depth-wise
  // warning: "VARCHAR", // Specifies if there is a warning on the position
  // warningNumber: "NUMERIC", // If there is a warning, The number of that warning
  desc1: "VARCHAR", // 1/4 Pallet ID
  desc2: "VARCHAR", // 1/2 Pallet ID
  desc3: "VARCHAR", // Pallet ID
  desc4: "VARCHAR", // One Touch Rack ID
  desc5: "VARCHAR", // ShipperShipper ID
  desc6: "VARCHAR", // SRI Requested Date
  desc7: "VARCHAR", // SRI Comment
  desc8: "VARCHAR", // PPI Comment
  desc9: "VARCHAR", // No description
  desc10: "VARCHAR", // PositionChangeType
  desc11: "VARCHAR", // PreviousShelf
  desc12: "VARCHAR", // No description
  desc13: "VARCHAR", // No description
  desc14: "VARCHAR", // No description
  desc15: "VARCHAR", // No description
  desc16: "VARCHAR", // No description
  desc17: "VARCHAR", // No description
  desc18: "VARCHAR", // No description
  desc19: "VARCHAR", // No description
  desc20: "VARCHAR", // No description
  desc21: "VARCHAR", // No description
  desc22: "VARCHAR", // No description
  desc23: "VARCHAR", // No description
  desc24: "VARCHAR", // No description
  desc25: "VARCHAR", // No description
  desc26: "VARCHAR", // No description
  desc27: "VARCHAR", // No description
  desc28: "VARCHAR", // No description
  desc29: "VARCHAR", // No description
  desc30: "VARCHAR", // No description
  value1: "NUMERIC", // PositionXChange
  value2: "NUMERIC", // PositionYChange
  value3: "NUMERIC", // PositionFacingChange
  value4: "NUMERIC", // PositionRowHighChange
  value5: "NUMERIC", // PreviousOrientation
  value6: "NUMERIC", // PreviousMerchStyle
  value7: "NUMERIC", // PreviousSegment
  value8: "NUMERIC", // PPI
  value9: "NUMERIC", // LEB Exception
  value10: "NUMERIC", // No description
  value11: "NUMERIC", // No description
  value12: "NUMERIC", // No description
  value13: "NUMERIC", // No description
  value14: "NUMERIC", // No description
  value15: "NUMERIC", // No description
  value16: "NUMERIC", // No description
  value17: "NUMERIC", // No description
  value18: "NUMERIC", // No description
  value19: "NUMERIC", // No description
  value20: "NUMERIC", // No description
  value21: "NUMERIC", // No description
  value22: "NUMERIC", // No description
  value23: "NUMERIC", // No description
  value24: "NUMERIC", // No description
  value25: "NUMERIC", // No description
  value26: "NUMERIC", // No description
  value27: "NUMERIC", // No description
  value28: "NUMERIC", // No description
  value29: "NUMERIC", // No description
  value30: "NUMERIC", // No description
  flag1: "INT", // Quarter Pallet
  flag2: "INT", // Half Pallet
  flag3: "INT", // DRP
  flag4: "INT", // One Touch Rack
  flag5: "INT", // Shipper
  flag6: "INT", // Store Requested Item
  flag7: "INT", // RRP
  flag8: "INT", // Wine Flag
  flag9: "INT", // No description
  flag10: "INT", // Security Case

  field148: "NUMERIC",
  field149: "NUMERIC",
  field150: "NUMERIC",
  field151: "NUMERIC",
  field152: "NUMERIC",
  field153: "NUMERIC",
  field154: "NUMERIC",
  field155: "NUMERIC",
  field156: "NUMERIC",
  field157: "NUMERIC",
  field158: "VARCHAR",
  field159: "VARCHAR",
  field160: "NUMERIC",
  field161: "VARCHAR",
  field162: "NUMERIC",
  field163: "NUMERIC",
  field164: "NUMERIC",
  field165: "NUMERIC",
  field166: "NUMERIC",
  // locationId: "INT", // The location ID given to the position
  // useTargetSpaceX: "INT", // Specifies whether the horizontal Target Space option is being used
  // useTargetSpaceY: "INT", // Specifies whether the vertical Target Space option is being used
  // useTargetSpaceZ: "INT", // Specifies whether the depth Target Space option is being used
  // targetSpaceX: "NUMERIC", // The amount of horizontal space to be filled by the position
  // targetSpaceY: "NUMERIC", // The amount of vertical space to be filled by the position
  // targetSpaceZ: "NUMERIC", // The amount of depth space to be filled by the position
  // capacity: "NUMERIC", // The total number of units that occupy this position on the planogram
  // units: "NUMERIC", // The number of units of the position (entered manually)
  // linear: "NUMERIC", // The total linear space occupied by the position
  // square: "NUMERIC", // The total square space occupied by the position.
  // cubic: "NUMERIC", // The total cubic space for this position
  // targetInventory: "NUMERIC", // The recommended number of units for this position based on the inventory model settings
  // facings: "NUMERIC", // The total number of front units for this position
  // indexInPlanogram: "NUMERIC", // The number of the position in the planogram, indicating the order in which positions were added to the planogram
  // segment: "NUMERIC", // The segment in which the position’s midpoint falls
  // firstSegment: "NUMERIC", // The first segment in which the position is displayed
  // lastSegment: "NUMERIC", // The last segment in which the position is displayed
  // imageSource: "VARCHAR", // The image associated with the position
  // pegIdUsed: "VARCHAR", // The actual peg used for the position when placed on a pegboard, multi-row pegboard, or hanging bar
  // replenishmentMin: "INT", // The minimum number of units required to require an automatic restocking for the product in the store
  // replenishmentMax: "INT", // The maximum number of units allowed to require an automatic restocking for the product in the store
  // shapeId: "VARCHAR", // The shape associated with the individual position. This field overrides the specified shape for the product and allows you to display different shapes for multiple positions of the same product.
  // bitmapIdOverride: "VARCHAR", // The image associated with the individual position. This field overrides the specified image for the product and allows you to display different images for multiple positions of the same product.
  // hideIfPrinting: "INT", // Specifies whether to draw the position when printing a Front, Side, or Top view
  // partId: "VARCHAR", // An additional field for storing an identifier of the position
  // bitmapIdOverrideUnit: "INT", // Specifies whether the image for the position represents a single unit. When selected, if the Show Units Within Merchandising Style option is selected, the unit image is copied high, wide, or deep when the product is merchandised in a tray or case.
  // capacityUnrestricted: "NUMERIC", // The number of units that would fit for the position if no other merchandising restrictions (such as Max High) were applied.
  // automaticModel: "INT", // Specifies how to draw automatic models for positions of the product, when using the Jar, Can, or Bottle merchandising style and specific models are not assigned for the product or position
  sentinel: "VARCHAR",
};
